import { useState } from "react";
import { Loader2, Eye, Play, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DEFAULT_URL = "https://wm.lunatik.cloud/api/migration/bins";
const DEFAULT_BANNED_URL = "https://wm.lunatik.cloud/api/migration/banned";

interface MigrationResult {
  source: { warehouses: number; shelves: number; bins: number; sku_locations: number };
  warehouses: { created: number; skipped: number };
  bins: { created: number; skipped: number; bad: number; relabeled: number };
  sku_locations: {
    created: number;
    skipped: number;
    bad_name: number;
    orphan: number;
  };
  dry_run: boolean;
  source_url: string;
}

async function runMigration(sourceUrl: string, dryRun: boolean): Promise<MigrationResult> {
  const res = await fetch(`/api/migration/lunatik?dry_run=${dryRun ? 1 : 0}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source_url: sourceUrl }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || "Greška pri migraciji");
  return data as MigrationResult;
}

export function MigrationSection() {
  const [sourceUrl, setSourceUrl] = useState(DEFAULT_URL);
  const [loading, setLoading] = useState<"preview" | "run" | null>(null);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handlePreview() {
    setLoading("preview");
    setError(null);
    try {
      const r = await runMigration(sourceUrl, true);
      setResult(r);
    } catch (e: any) {
      setError(e?.message || "Greška");
      setResult(null);
    } finally {
      setLoading(null);
    }
  }

  async function handleRun() {
    setConfirmOpen(false);
    setLoading("run");
    setError(null);
    try {
      const r = await runMigration(sourceUrl, false);
      setResult(r);
      toast.success("Migracija završena");
    } catch (e: any) {
      setError(e?.message || "Greška");
      toast.error(e?.message || "Greška pri migraciji");
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <BannedMigrationSection />
      <div className="ring-1 ring-foreground/10">
        <div className="border-b bg-muted/50 px-4 py-2">
          <span className="text-xs font-medium">Migracija sa starog sistema (lunatik)</span>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <p className="text-xs text-muted-foreground max-w-xl">
            Uvozi skladišta, police, lokacije i SKU rasporede iz starog sistema.
            Operacija je idempotentna — možeš je pokrenuti više puta, postojeći
            redovi se ne diraju.
          </p>

          <div className="flex flex-col gap-1.5 max-w-xl">
            <Label htmlFor="migration-source" className="text-xs">
              Source URL
            </Label>
            <Input
              id="migration-source"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder={DEFAULT_URL}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
          </div>

          <div className="flex gap-2 mt-1">
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={loading !== null || !sourceUrl}
            >
              {loading === "preview" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Eye className="mr-2 h-4 w-4" />
              )}
              Provjeri (dry-run)
            </Button>

            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={loading !== null || !sourceUrl || !result}
            >
              {loading === "run" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Pokreni migraciju
            </Button>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 text-red-700 dark:text-red-400 px-3 py-2 text-xs">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {result && <ResultView result={result} />}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pokrenuti migraciju?</DialogTitle>
            <DialogDescription>
              Operacija će upisati podatke iz starog sistema u trenutnu bazu.
              Idempotentna je — ne briše postojeće, samo dodaje ono što fali.
              Backup baze prije pokretanja je preporučljiv.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Odustani
            </Button>
            <Button onClick={handleRun}>Pokreni</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResultView({ result }: { result: MigrationResult }) {
  const wTotal = result.warehouses.created + result.warehouses.skipped;
  const bTotal = result.bins.created + result.bins.skipped + result.bins.bad;
  const lTotal =
    result.sku_locations.created +
    result.sku_locations.skipped +
    result.sku_locations.bad_name +
    result.sku_locations.orphan;

  return (
    <div className="ring-1 ring-foreground/10 mt-2">
      <div className="border-b bg-muted/50 px-3 py-1.5 flex items-center gap-2">
        {result.dry_run ? (
          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Dry run — ništa nije sačuvano
          </span>
        ) : (
          <>
            <CheckCircle2 className="h-3 w-3 text-green-600" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-green-700 dark:text-green-400">
              Migracija izvršena
            </span>
          </>
        )}
      </div>

      <table className="w-full text-xs">
        <thead className="bg-muted/30 text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-1.5 font-medium">Entitet</th>
            <th className="text-right px-3 py-1.5 font-medium">U source-u</th>
            <th className="text-right px-3 py-1.5 font-medium">Novo</th>
            <th className="text-right px-3 py-1.5 font-medium">Postoji</th>
            <th className="text-right px-3 py-1.5 font-medium text-amber-600 dark:text-amber-500">
              Preskočeno
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t">
            <td className="px-3 py-1.5 font-medium">Skladišta</td>
            <td className="px-3 py-1.5 text-right tabular-nums">{result.source.warehouses}</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-green-700 dark:text-green-400">
              {result.warehouses.created}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
              {result.warehouses.skipped}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">—</td>
          </tr>
          <tr className="border-t">
            <td className="px-3 py-1.5 font-medium">
              Bin-ovi
              <span className="text-muted-foreground"> ({result.source.shelves} polica)</span>
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums">{result.source.bins}</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-green-700 dark:text-green-400">
              {result.bins.created}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
              {result.bins.skipped}
            </td>
            <td
              className="px-3 py-1.5 text-right tabular-nums text-amber-600 dark:text-amber-500"
              title="Bin-ovi sa neispravnim imenom"
            >
              {result.bins.bad}
            </td>
          </tr>
          <tr className="border-t">
            <td className="px-3 py-1.5 font-medium">SKU lokacije</td>
            <td className="px-3 py-1.5 text-right tabular-nums">{result.source.sku_locations}</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-green-700 dark:text-green-400">
              {result.sku_locations.created}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
              {result.sku_locations.skipped}
            </td>
            <td
              className="px-3 py-1.5 text-right tabular-nums text-amber-600 dark:text-amber-500"
              title={`${result.sku_locations.bad_name} sa SKU/barkod imenom + ${result.sku_locations.orphan} orphana`}
            >
              {result.sku_locations.bad_name + result.sku_locations.orphan}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="border-t px-3 py-2 text-[11px] text-muted-foreground flex flex-col gap-0.5">
        <span>
          Procesovano: {wTotal} skladišta, {bTotal} bin-ova, {lTotal} SKU lokacija
        </span>
        {result.sku_locations.bad_name > 0 && (
          <span>
            <span className="font-medium">{result.sku_locations.bad_name}</span> SKU lokacija
            preskočeno jer im je <code>bin_name</code> u stvari SKU/barkod, ne pozicija.
          </span>
        )}
        {result.sku_locations.orphan > 0 && (
          <span>
            <span className="font-medium">{result.sku_locations.orphan}</span> orphan lokacija
            (bin obrisan u starom sistemu, lokacija ostala).
          </span>
        )}
        {result.bins.relabeled > 0 && (
          <span>
            <span className="font-medium">{result.bins.relabeled}</span> bin-ova ažurirano s
            originalnim imenom police.
          </span>
        )}
      </div>
    </div>
  );
}

interface BannedMigrationResult {
  source: { count: number };
  created: number;
  skipped: number;
  bad: number;
  dry_run: boolean;
  source_url: string;
}

async function runBannedMigration(
  sourceUrl: string,
  dryRun: boolean,
): Promise<BannedMigrationResult> {
  const res = await fetch(`/api/migration/banned?dry_run=${dryRun ? 1 : 0}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ source_url: sourceUrl }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || "Greška pri migraciji");
  return data as BannedMigrationResult;
}

function BannedMigrationSection() {
  const [sourceUrl, setSourceUrl] = useState(DEFAULT_BANNED_URL);
  const [loading, setLoading] = useState<"preview" | "run" | null>(null);
  const [result, setResult] = useState<BannedMigrationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handlePreview() {
    setLoading("preview");
    setError(null);
    try {
      const r = await runBannedMigration(sourceUrl, true);
      setResult(r);
    } catch (e: any) {
      setError(e?.message || "Greška");
      setResult(null);
    } finally {
      setLoading(null);
    }
  }

  async function handleRun() {
    setConfirmOpen(false);
    setLoading("run");
    setError(null);
    try {
      const r = await runBannedMigration(sourceUrl, false);
      setResult(r);
      toast.success("Migracija banovanih kupaca završena");
    } catch (e: any) {
      setError(e?.message || "Greška");
      toast.error(e?.message || "Greška pri migraciji");
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="ring-1 ring-foreground/10">
      <div className="border-b bg-muted/50 px-4 py-2">
        <span className="text-xs font-medium">Migracija banovanih kupaca (lunatik)</span>
      </div>
      <div className="p-4 flex flex-col gap-3">
        <p className="text-xs text-muted-foreground max-w-xl">
          Uvozi listu banovanih kupaca iz starog sistema. Dedup po emailu i
          telefonu — postojeći banovi se ne diraju.
        </p>

        <div className="flex flex-col gap-1.5 max-w-xl">
          <Label htmlFor="banned-migration-source" className="text-xs">
            Source URL
          </Label>
          <Input
            id="banned-migration-source"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder={DEFAULT_BANNED_URL}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
        </div>

        <div className="flex gap-2 mt-1">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={loading !== null || !sourceUrl}
          >
            {loading === "preview" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Eye className="mr-2 h-4 w-4" />
            )}
            Provjeri (dry-run)
          </Button>

          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={loading !== null || !sourceUrl || !result}
          >
            {loading === "run" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Pokreni migraciju
          </Button>
        </div>

        {error && (
          <div className="flex items-start gap-2 bg-red-500/10 text-red-700 dark:text-red-400 px-3 py-2 text-xs">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="ring-1 ring-foreground/10 mt-2">
            <div className="border-b bg-muted/50 px-3 py-1.5 flex items-center gap-2">
              {result.dry_run ? (
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                  Dry run — ništa nije sačuvano
                </span>
              ) : (
                <>
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-green-700 dark:text-green-400">
                    Migracija izvršena
                  </span>
                </>
              )}
            </div>
            <table className="w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-1.5 font-medium">Entitet</th>
                  <th className="text-right px-3 py-1.5 font-medium">U source-u</th>
                  <th className="text-right px-3 py-1.5 font-medium">Novo</th>
                  <th className="text-right px-3 py-1.5 font-medium">Postoji</th>
                  <th className="text-right px-3 py-1.5 font-medium text-amber-600 dark:text-amber-500">
                    Bez kontakta
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="px-3 py-1.5 font-medium">Banovani kupci</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{result.source.count}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-green-700 dark:text-green-400">
                    {result.created}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                    {result.skipped}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-amber-600 dark:text-amber-500">
                    {result.bad}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pokrenuti migraciju banovanih kupaca?</DialogTitle>
            <DialogDescription>
              Operacija će uvesti banovane kupce iz starog sistema. Dedup po
              emailu i telefonu — postojeći redovi se ne diraju.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Odustani
            </Button>
            <Button onClick={handleRun}>Pokreni</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
